import { Link, useLocation } from '@tanstack/react-router';
import {
  CreditCard,
  Gauge,
  History,
  KeyRound,
  LayoutDashboard,
  Receipt,
  Settings,
  UserCog,
  Users,
  Wallet,
} from 'lucide-react';
import type { ComponentType } from 'react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '../ui/sidebar';
import { useUser } from '../../lib/auth/hooks';
import type { UserRole } from '@runik/shared';

interface NavItem {
  label: string;
  to: string;
  icon: ComponentType<{ className?: string }>;
  roles: UserRole[];
}

const NAV: NavItem[] = [
  { label: 'Panel', to: '/dashboard', icon: LayoutDashboard, roles: ['admin', 'customer'] },
  { label: 'Müşteriler', to: '/admin/customers', icon: Users, roles: ['admin'] },
  { label: 'Kimlik Bilgileri', to: '/admin/credentials', icon: KeyRound, roles: ['admin'] },
  { label: 'Ödemeler', to: '/admin/payments', icon: Receipt, roles: ['admin'] },
  { label: 'Kullanım', to: '/admin/usage', icon: Gauge, roles: ['admin'] },
  { label: 'Denetim Kaydı', to: '/admin/audit', icon: History, roles: ['admin'] },
  { label: 'Yöneticiler', to: '/admin/admins', icon: UserCog, roles: ['admin'] },
  { label: 'Ayarlar', to: '/admin/settings', icon: Settings, roles: ['admin'] },
  { label: 'Ödemelerim', to: '/payments', icon: CreditCard, roles: ['customer'] },
  { label: 'Kullanım', to: '/usage', icon: Wallet, roles: ['customer'] },
];

export function AppSidebar() {
  const { data: user } = useUser();
  const location = useLocation();
  const role: UserRole | undefined = user?.role;
  const items = NAV.filter((n) => (role ? n.roles.includes(role) : false));

  return (
    <Sidebar>
      <SidebarHeader>
        <Link to="/dashboard" className="flex items-center gap-2 text-lg font-semibold text-brand">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-brand text-white">R</div>
          Runik
        </Link>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Genel</SidebarGroupLabel>
          <SidebarMenu>
            {items.map((item) => {
              const isActive =
                item.to === '/dashboard'
                  ? location.pathname === item.to
                  : location.pathname.startsWith(item.to);
              return (
                <SidebarMenuItem key={item.to}>
                  <SidebarMenuButton asChild isActive={isActive}>
                    <Link to={item.to}>
                      <item.icon className="h-4 w-4" />
                      <span>{item.label}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <div className="text-xs text-slate-500 truncate">
          {user?.email}
          <div className="text-[10px] uppercase tracking-wider">{user?.role}</div>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
